/**
 * Metadados e agrupamento dos filtros de condição das regras de automação.
 */

import {
  CLIENT_FIELD_GROUPS,
  isClientFieldConditionKey,
  listAvailableClientFieldPresets,
} from "./automationClientFieldCatalog";

const SCHEMA_KEY_META = {
  origensdeleads: {
    category: "solicitacao",
    title: "Origem da solicitação",
    description: "Canal ou origem de entrada (WhatsApp, e-mail, site, etc.).",
    keywords: ["origem", "canal", "whatsapp", "email"],
  },
  tiposdeleads: {
    category: "solicitacao",
    title: "Tipo da solicitação",
    description: "Categoria principal do atendimento.",
    keywords: ["tipo", "categoria"],
  },
  subtiposdeleads1: {
    category: "solicitacao",
    title: "Subtipo 1",
    description: "Primeiro nível de subcategoria.",
    keywords: ["subtipo"],
  },
  subtiposdeleads2: {
    category: "solicitacao",
    title: "Subtipo 2",
    description: "Segundo nível de subcategoria.",
    keywords: ["subtipo"],
  },
  subtiposdeleads3: {
    category: "solicitacao",
    title: "Subtipo 3",
    description: "Terceiro nível de subcategoria.",
    keywords: ["subtipo"],
  },
  subtiposdeleads: {
    category: "solicitacao",
    title: "Subtipo",
    description: "Subcategoria da solicitação.",
    keywords: ["subtipo"],
  },
  formasdepagamento: {
    category: "solicitacao",
    title: "Forma de pagamento",
    description: "Meio de pagamento vinculado ao pedido ou solicitação.",
    keywords: ["pagamento", "pix", "cartão"],
  },
  usuarios: {
    category: "solicitacao",
    title: "Responsável pelo atendimento",
    description: "Usuário ou equipe atribuída à solicitação.",
    keywords: ["usuário", "atendente", "responsável", "operador"],
  },
  statuslead: {
    category: "solicitacao",
    title: "Status da solicitação",
    description: "Situação atual (pendente, em atendimento, perdida, etc.).",
    keywords: ["status", "perdida", "concluída"],
  },
  taglead: {
    category: "solicitacao",
    title: "Tags da solicitação",
    description: "Tem uma ou mais tags no atendimento. Escolha se precisa de todas ou de pelo menos uma.",
    keywords: ["tag", "etiqueta", "todas", "pelo menos uma"],
  },
  tagcliente: {
    category: "cliente",
    title: "Tags do cadastro do cliente",
    description: "Tem uma ou mais tags no cadastro. Escolha se precisa de todas ou de pelo menos uma.",
    keywords: ["tag", "cliente", "etiqueta", "cadastro", "todas", "pelo menos uma"],
  },
  nivel_consciencia: {
    category: "cliente",
    title: "Nível de consciência",
    description: "Etapa mental do cliente na jornada (clientesgrupos).",
    keywords: ["consciência", "awareness", "schwartz", "estágio"],
  },
  schwartz_level: {
    category: "cliente",
    title: "Nível Schwartz (1-5)",
    description: "Framework Eugene Schwartz: 1=inconsciente até 5=pronto para comprar.",
    keywords: ["schwartz", "consciência", "funil"],
  },
  funnel_phase: {
    category: "cliente",
    title: "Fase do funil",
    description: "Topo, meio, fundo, pós-venda, retenção ou expansão.",
    keywords: ["funil", "topo", "fundo", "retenção"],
  },
  tempo_no_estagio: {
    category: "cliente",
    title: "Dias no estágio atual",
    description: "Tempo mínimo desde a última mudança de nível de consciência.",
    keywords: ["inatividade", "estágio", "dias"],
  },
  canal_origem: {
    category: "solicitacao",
    title: "Canal de origem",
    description: "Canal de entrada da solicitação (WhatsApp, e-mail, site…).",
    keywords: ["canal", "origem", "whatsapp"],
  },
  webchat_ativo: {
    category: "solicitacao",
    title: "Web Chat ativo",
    description:
      "A solicitação está com sessão de Web Chat ligada (widget do site ou link WhatsApp → Web Chat).",
    keywords: ["webchat", "web chat", "widget", "chat no site", "sessão"],
  },
  campanhas: {
    category: "solicitacao",
    title: "Campanha (disparo)",
    description: "Solicitação gerada ou atribuída a um disparo omnichannel.",
    keywords: ["campanha", "disparo", "broadcast", "whatsapp", "email"],
  },
  campanhas_whatsapp: {
    category: "solicitacao",
    title: "Campanha WhatsApp (legado)",
    description: "Solicitação ligada a campanha WhatsApp legada.",
    keywords: ["campanha", "whatsapp", "legado", "disparo"],
  },
  tem_nf: {
    category: "outros",
    title: "Nota fiscal informada",
    description: "Quando a solicitação possui ou não número de NF.",
    keywords: ["nf", "nota fiscal"],
  },
  whatsapp_origem_anuncio: {
    category: "outros",
    title: "Veio de anúncio WhatsApp (CTWA)",
    description: "Conversa iniciada por clique em anúncio Click-to-WhatsApp.",
    keywords: ["anúncio", "ctwa", "meta", "whatsapp"],
  },
  textocom: {
    category: "outros",
    title: "Texto contém",
    description: "Resposta ou mensagem contém o texto informado.",
    keywords: ["texto", "contém", "mensagem"],
  },
  textoigual: {
    category: "outros",
    title: "Texto é igual a",
    description: "Resposta ou mensagem igual ao texto informado.",
    keywords: ["texto", "igual"],
  },
  valor_minimo: {
    category: "outros",
    title: "Valor mínimo",
    description: "Valor do pedido acima do limite.",
    keywords: ["valor", "pedido"],
  },
  valor_maximo: {
    category: "outros",
    title: "Valor máximo",
    description: "Valor do pedido abaixo do limite.",
    keywords: ["valor", "pedido"],
  },
};

const CATEGORY_META = {
  solicitacao: {
    title: "Solicitação",
    description: "Origem, tipo, status, responsável e classificação do atendimento.",
  },
  cliente: {
    title: "Cliente",
    description: "Dados e etiquetas do cadastro vinculado.",
  },
  cliente_dados: {
    title: "Dados do cadastro do cliente",
    description: "Mesma lógica dos identificadores do ChatBot: campo preenchido ou ainda ausente.",
  },
  outros: {
    title: "Outros critérios",
    description: "Texto, valores, NF e origem de anúncio.",
  },
};

function normalizeLegacyLabel(label, key) {
  const raw = String(label || "").trim();
  if (!raw) return SCHEMA_KEY_META[key]?.title || key;

  const deQuais = raw.match(/^De quais\s+(.+?)\??$/i);
  if (deQuais) {
    const subject = deQuais[1].trim();
    const map = {
      origens: "Origem da solicitação",
      tipos: "Tipo da solicitação",
      "subtipos 1": "Subtipo 1",
      "subtipos 2": "Subtipo 2",
      "subtipos 3": "Subtipo 3",
      "formas de pagamento": "Forma de pagamento",
      usuários: "Responsável pelo atendimento",
      usuarios: "Responsável pelo atendimento",
      status: "Status da solicitação",
      "tags da solicitação": "Tags da solicitação",
      "tags do cliente": "Tags do cliente",
    };
    const lower = subject.toLowerCase();
    return map[lower] || subject.charAt(0).toUpperCase() + subject.slice(1);
  }

  return raw;
}

export function getSchemaConditionMeta(key, def = {}) {
  const meta = SCHEMA_KEY_META[key] || {};
  return {
    category: meta.category || "outros",
    title: meta.title || normalizeLegacyLabel(def.label, key),
    description: meta.description || def.helperText || def.hint || "Configure os valores permitidos para este filtro.",
    keywords: meta.keywords || [],
  };
}

export function getConditionAccordionTitle(key, def = {}, labelOverride = "") {
  if (isClientFieldConditionKey(key)) {
    if (labelOverride) return String(labelOverride);
    return def?.label || key;
  }
  return getSchemaConditionMeta(key, def).title;
}

function matchesSearch(text, query) {
  if (!query) return true;
  return String(text || "")
    .toLowerCase()
    .includes(query.toLowerCase());
}

/**
 * Monta grupos para o seletor de filtros.
 * @param {object} opts
 * @param {object} opts.eventSchema
 * @param {Set<string>} [opts.usedKeys]
 * @param {Set<string>} [opts.hiddenKeys] — chaves que não devem aparecer (ex.: campanhas sem itens)
 */
export function buildConditionPickerGroups({
  eventSchema = {},
  usedKeys = new Set(),
  hiddenKeys = new Set(),
} = {}) {
  const groupsById = new Map();

  const ensureGroup = (categoryId) => {
    if (!groupsById.has(categoryId)) {
      const meta = CATEGORY_META[categoryId] || { title: categoryId, description: "" };
      groupsById.set(categoryId, {
        id: categoryId,
        title: meta.title,
        description: meta.description,
        schemaItems: [],
        clientGroups: [],
      });
    }
    return groupsById.get(categoryId);
  };

  for (const [key, def] of Object.entries(eventSchema)) {
    if (key === "schedule_interval" || key === "schedule_run_window" || isClientFieldConditionKey(key)) continue;
    if (usedKeys.has(key)) continue;
    if (hiddenKeys.has(key)) continue;

    const meta = getSchemaConditionMeta(key, def);
    const group = ensureGroup(meta.category);
    group.schemaItems.push({
      type: "schema",
      key,
      def,
      title: meta.title,
      description: meta.description,
      searchText: [meta.title, meta.description, key, ...(meta.keywords || [])].join(" "),
    });
  }

  const availablePresets = listAvailableClientFieldPresets(usedKeys);
  const presetsByField = new Map();
  for (const preset of availablePresets) {
    const list = presetsByField.get(preset.fieldKey || preset.key) || [];
    list.push(preset);
    presetsByField.set(preset.fieldKey || preset.key, list);
  }

  for (const fieldGroup of CLIENT_FIELD_GROUPS) {
    const presets = presetsByField.get(fieldGroup.fieldKey);
    if (!presets?.length) continue;
    const group = ensureGroup("cliente_dados");
    group.clientGroups.push({
      ...fieldGroup,
      presets: presets.sort((a, b) => Number(b.value) - Number(a.value)),
    });
  }

  const order = ["solicitacao", "cliente_dados", "cliente", "outros"];
  return order
    .map((id) => groupsById.get(id))
    .filter(Boolean)
    .filter((g) => g.schemaItems.length > 0 || g.clientGroups.length > 0);
}

export function filterConditionPickerGroups(groups, searchQuery = "") {
  const q = String(searchQuery || "").trim();
  if (!q) return groups;

  return groups
    .map((group) => {
      const schemaItems = group.schemaItems.filter((item) => matchesSearch(item.searchText, q));
      const clientGroups = group.clientGroups
        .map((cg) => ({
          ...cg,
          presets: cg.presets.filter(
            (p) =>
              matchesSearch(
                [cg.title, cg.description, p.label, p.hint, p.shortLabel].join(" "),
                q
              )
          ),
        }))
        .filter((cg) => cg.presets.length > 0);

      if (!schemaItems.length && !clientGroups.length) return null;
      return { ...group, schemaItems, clientGroups };
    })
    .filter(Boolean);
}

export function countConditionPickerItems(groups) {
  return groups.reduce(
    (acc, g) => acc + g.schemaItems.length + g.clientGroups.reduce((s, cg) => s + cg.presets.length, 0),
    0
  );
}
